import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { uploadMultipleFiles } from '../../../services/storageService';
import toast from 'react-hot-toast';

const FileUploadZone = ({ files, onFilesChange, errors, caseId, userId }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e?.dataTransfer?.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e?.target?.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles?.filter((file) => {
      const isImage = file?.type?.startsWith('image/');
      const isPDF = file?.type === 'application/pdf';
      const isValidSize = file?.size <= 50 * 1024 * 1024;
      return (isImage || isPDF) && isValidSize;
    });

    if (validFiles?.length !== newFiles?.length) {
      toast?.error(`${newFiles?.length - validFiles?.length} file(s) rejected. Only images and PDFs under 50MB are allowed.`);
    }

    const updatedFiles = [...files, ...validFiles]?.slice(0, 35);
    onFilesChange(updatedFiles);

    if (updatedFiles?.length > 35) {
      toast?.error('Maximum 35 files allowed. Extra files were not added.');
    }
  };

  const handleUpload = async () => {
    if (!caseId || !userId) {
      toast?.error('Case ID and User ID required for upload');
      return;
    }

    if (files?.length === 0) {
      toast?.error('No files to upload');
      return;
    }

    setIsUploading(true);
    setUploadProgress({ current: 0, total: files?.length, percentage: 0 });

    try {
      const result = await uploadMultipleFiles(userId, caseId, files, (progress) => {
        setUploadProgress(progress);
      });

      if (result?.success) {
        toast?.success(`Successfully uploaded ${result?.summary?.success} file(s)`);
        onFilesChange([]);
        setUploadProgress(null);
      } else {
        toast?.error(`Upload completed with ${result?.summary?.failed} error(s)`);
      }
    } catch (error) {
      toast?.error('Upload failed: ' + error?.message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = files?.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition-all duration-250 ${
          isDragging
            ? 'border-primary bg-primary/5' :'border-border bg-muted/30 hover:border-primary/50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf"
          onChange={handleFileInput}
          className="hidden"
          disabled={isUploading}
        />

        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Upload" size={32} className="text-primary" />
          </div>

          <div>
            <h3 className="text-lg md:text-xl font-semibold mb-2">
              Drop files here or click to browse
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload up to 30 images (JPG, PNG, HEIC) and optional PDFs
              <br />
              Maximum file size: 50MB per file
              <br />
              <span className="text-xs text-primary font-medium">Images will be automatically compressed and EXIF data removed for privacy</span>
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => fileInputRef?.current?.click()}
            iconName="FolderOpen"
            iconPosition="left"
            disabled={isUploading}
          >
            Select Files
          </Button>
        </div>
      </div>

      {uploadProgress && (
        <div className="p-4 rounded-lg border border-primary/20 bg-primary/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Uploading files...</span>
            <span className="text-sm text-muted-foreground">
              {uploadProgress?.current} / {uploadProgress?.total}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress?.percentage}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Processing: compression, EXIF removal, thumbnail generation
          </p>
        </div>
      )}
      {errors?.files && (
        <div className="flex items-center gap-2 text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          <span>{errors?.files}</span>
        </div>
      )}
      {files?.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-base md:text-lg">
              Selected Files ({files?.length}/35)
            </h4>
            <div className="flex gap-2">
              {caseId && userId && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleUpload}
                  iconName="Upload"
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload All'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onFilesChange([])}
                iconName="Trash2"
                disabled={isUploading}
              >
                Clear All
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
            {files?.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 md:p-4 rounded-lg border border-border bg-card"
              >
                <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-muted flex items-center justify-center">
                  <Icon
                    name={file?.type?.startsWith('image/') ? 'Image' : 'FileText'}
                    size={20}
                    className="text-muted-foreground"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm md:text-base truncate">
                    {file?.name}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {formatFileSize(file?.size)}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFile(index)}
                  iconName="X"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-start gap-2 p-4 rounded-lg bg-warning/10 border border-warning/20">
        <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-warning mb-1">Important Upload Guidelines</p>
          <ul className="text-muted-foreground space-y-1 list-disc list-inside">
            <li>Include photos of fire origin area, burn patterns, and damage extent</li>
            <li>Capture electrical panels, appliances, and potential ignition sources</li>
            <li>Document scene from multiple angles with scale references</li>
            <li>Upload any relevant incident reports, witness statements, or diagrams as PDFs</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FileUploadZone;